// /join-a-network/:slug — public ad-entry landing for the "join-network"
// journey, keyed by a group join-link slug. Same resolution contract as
// /join/:slug (preview query, signed-in redirect to the group page, precise
// unknown/retired-link states) but signed-out visitors get the ad-matched
// pitch instead of an immediate redirect. The bare /join-a-network sibling
// serves the same pitch without a target network.

import { useEffect } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Button, Container, EmptyState, LoadingState } from '@leanin/ui';
import { EntryJourneyLanding } from '@/components/entry-journeys';
import { useEntryJourneyCopy } from '@/components/entry-journeys/journey-copy';
import { JoinLinkLanding } from '@/components/groups/JoinLinkLanding';
import { writePostOnboardingGroup } from '@/components/onboarding/post-onboarding-group';
import { writeEntryJourney } from '@/lib/entry-journey';
import { groupKeys } from '@/lib/queries/groups';
import { useSession } from '@/lib/use-session';
import { navigateArgsForGroup } from '@/lib/group-url';
import {
  isValidReferralCode,
  refSearchParam,
  storeSignupRef,
} from '@/lib/signup-ref-cookie';

export const Route = createFileRoute('/_public/join-a-network/$slug')({
  validateSearch: (search: Record<string, unknown>) => refSearchParam(search),
  // A signed-in visitor skips the ad pitch — resolved here, before render,
  // because the _public layout skipped its onboarding gate for this path on
  // the promise that the route re-applies it itself. An active link sends a
  // mid-onboarding member into the journey onboarding with the network
  // stashed as the post-onboarding destination (honouring the ad's promise),
  // and an onboarded member straight to the group page, exactly like
  // /join/:slug. A link that can't resolve (malformed, unknown, retired,
  // fetch failure) honours the generic gate instead — without this a
  // mid-onboarding member would land on the error state with full app
  // chrome, having skipped onboarding entirely. No cookie for signed-in
  // visitors (same shared-browser rationale as /join/:slug).
  beforeLoad: async ({ context, params }) => {
    if (context.session === null) return;
    const needsOnboarding =
      context.member !== null &&
      context.member.onboardingCompletedAt === null;
    let preview = null;
    if (isValidReferralCode(params.slug)) {
      try {
        preview = await context.queryClient.ensureQueryData(
          groupKeys.joinLinkPreview(params.slug),
        );
      } catch {
        // Unresolvable link — treated the same as an unknown one below;
        // the component renders the error state for onboarded members.
      }
    }
    const active = preview?.status === 'active' ? preview : null;
    if (active !== null) {
      if (needsOnboarding) {
        writeEntryJourney('join-network');
        writePostOnboardingGroup({
          kind: active.group.kind,
          id: active.group.id,
        });
        throw redirect({ to: '/onboarding/j/about', replace: true });
      }
      // Only redirect onto the group page when this member can actually open
      // it. A private or Network-only Network 404s a non-member, so the
      // component renders `JoinLinkLanding` with the join CTA instead — same
      // fix as /join/:slug.
      if (active.viewerCanOpen) {
        throw redirect({
          ...navigateArgsForGroup(
            active.group.kind,
            active.group.id,
            active.group.slug,
          ),
          replace: true,
        });
      }
    }
    if (needsOnboarding) {
      throw redirect({ to: '/onboarding/profile' });
    }
  },
  component: JoinNetworkEntryPage,
});

function JoinNetworkEntryPage() {
  const { slug } = Route.useParams();
  const { ref } = Route.useSearch();
  const navigate = useNavigate();
  const { data: ctx } = useSession();
  const journeyCopy = useEntryJourneyCopy('join-network');
  const validSlug = isValidReferralCode(slug);
  const preview = useQuery({
    ...groupKeys.joinLinkPreview(slug),
    enabled: validSlug,
  });

  const signedIn = Boolean(ctx);
  const active = preview.data?.status === 'active' ? preview.data : null;

  // Signed-in routing normally happens in beforeLoad; this only fires when
  // the preview fetch failed there but a client-side retry later resolved
  // the link (so only onboarded members can reach it — mid-onboarding ones
  // were gated before render).
  useEffect(() => {
    if (active === null || !signedIn) return;
    if (!active.viewerCanOpen) return;
    void navigate({
      ...navigateArgsForGroup(
        active.group.kind,
        active.group.id,
        active.group.slug,
      ),
      replace: true,
    });
  }, [active, signedIn, navigate]);

  // For signed-out visitors the join-link slug IS the attribution ref —
  // storing it powers both campaign attribution and the post-signup
  // auto-join at /auth/finishing. An explicit ?ref=CODE is stored first so
  // the slug (the stronger signal: it names the group to auto-join) wins
  // when both are present.
  useEffect(() => {
    if (signedIn) return;
    if (ref !== undefined) storeSignupRef(ref);
    if (active !== null) storeSignupRef(slug);
  }, [active, ref, signedIn, slug]);

  if (!validSlug || (preview.isSuccess && preview.data === null)) {
    return (
      <Container max="sm">
        <EmptyState
          density="page"
          icon="link"
          title="This join link isn’t valid"
          body="Check the link for typos, or ask the person who shared it to send it again."
          primary={
            <Button variant="primary" onClick={() => void navigate({ to: '/' })}>
              Explore Lean In
            </Button>
          }
        />
      </Container>
    );
  }

  if (preview.isError) {
    return (
      <Container max="sm">
        <EmptyState
          density="page"
          icon="link"
          title="Something went wrong"
          body="We couldn’t load this join link. Try refreshing the page."
        />
      </Container>
    );
  }

  if (preview.data?.status === 'inactive') {
    return (
      <Container max="sm">
        <EmptyState
          density="page"
          icon="link"
          title="This join link is no longer active"
          body={`The link to ${preview.data.group.name} has been turned off. Ask the group’s leaders for a new one.`}
          primary={
            <Button variant="primary" onClick={() => void navigate({ to: '/' })}>
              Explore Lean In
            </Button>
          }
        />
      </Container>
    );
  }

  if (active !== null && !signedIn) {
    // Same copy as the bare /join-a-network landing — the slug's only job
    // is attribution and the auto-join to this specific network.
    return <EntryJourneyLanding journey="join-network" {...journeyCopy} />;
  }

  // Signed-in, but the Network's own page would 404 this viewer (private or
  // Network-only) — the join CTA lives here rather than one redirect later.
  if (active !== null && !active.viewerCanOpen) {
    return <JoinLinkLanding target={active.group} signedIn />;
  }

  // Pending, or active-and-signed-in and about to redirect.
  return <LoadingState placement="page" label="Loading…" />;
}
